import React, { useState, useEffect } from 'react';
import { useCart } from '../component/CartContext';
import { auth, db } from '../FirebaseConfig';
import { addDoc, collection, Timestamp, doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import emailjs from 'emailjs-com';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Lock, FastForward, CheckCircle, Package, XCircle, MessageCircle, Phone } from 'lucide-react';
import { toast } from 'react-toastify';

// ──────────────────────────────────────────────────────────────
// Helper: build WhatsApp & SMS links for each seller
// ──────────────────────────────────────────────────────────────

/**
 * Cleans a Nigerian phone number to international format (234XXXXXXXXXX).
 * Handles:  08012345678  →  2348012345678
 *           +2348012345678  →  2348012345678
 *           2348012345678   →  2348012345678
 */
const toIntlPhone = (raw = '') => {
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('234')) return digits;
  if (digits.startsWith('0')) return '234' + digits.slice(1);
  return digits; // already clean or unknown format
};

/**
 * Builds per-seller contact links after a successful order.
 * Returns an array of { sellerPhone, waUrl, smsUrl, items[] }
 */
const buildSellerLinks = (cartItems, orderId, buyerName, buyerPhone) => {
  // Group items by seller phone
  const map = {};
  cartItems.forEach(item => {
    const phone = item.phoneNumber || '';
    if (!phone) return;
    if (!map[phone]) map[phone] = [];
    map[phone].push(item);
  });

  return Object.entries(map).map(([rawPhone, items]) => {
    const intlPhone = toIntlPhone(rawPhone);

    const itemLines = items
      .map(i => `• ${i.name} x${i.quantity} — ₦${(i.price * i.quantity).toLocaleString()}`)
      .join('\n');

    const message =
      `Hello! 👋 I just placed an order on uCart.\n\n` +
      `🧾 *Order ID:* ${orderId}\n` +
      `👤 *Buyer:* ${buyerName}\n` +
      `📞 *My Phone:* ${buyerPhone}\n\n` +
      `🛒 *Items:*\n${itemLines}\n\n` +
      `Please confirm receipt and let me know when to come for pickup. Thanks!`;

    const encodedMsg = encodeURIComponent(message);

    return {
      sellerPhone: rawPhone,
      intlPhone,
      waUrl: `https://wa.me/${intlPhone}?text=${encodedMsg}`,
      smsUrl: `sms:${rawPhone}?body=${encodedMsg}`,
      items,
    };
  });
};

// ──────────────────────────────────────────────────────────────
// Post-payment Contact Modal
// ──────────────────────────────────────────────────────────────

const ContactSellersModal = ({ sellerLinks, onDone }) => {
  const [contacted, setContacted] = useState(new Set());

  const handleWhatsApp = (link) => {
    window.open(link.waUrl, '_blank');
    setContacted(prev => new Set([...prev, link.sellerPhone]));
  };

  const handleSMS = (link) => {
    window.location.href = link.smsUrl;
    setContacted(prev => new Set([...prev, link.sellerPhone]));
  };

  const allContacted = sellerLinks.length > 0 && contacted.size >= sellerLinks.length;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="w-full max-w-md bg-[#0d1117] border border-green-500/30 rounded-3xl p-6 shadow-2xl shadow-green-900/40 text-white"
        initial={{ scale: 0.85, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0, y: 40 }}
        transition={{ type: 'spring', stiffness: 280, damping: 24 }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center">
            <CheckCircle size={20} className="text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-black text-green-400 leading-tight">Payment Successful!</h2>
            <p className="text-gray-400 text-xs">Now contact your seller(s) to arrange pickup</p>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          {sellerLinks.map((link, i) => {
            const done = contacted.has(link.sellerPhone);
            return (
              <motion.div
                key={link.sellerPhone}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`rounded-2xl border p-4 transition-all duration-300 ${
                  done
                    ? 'border-green-500/50 bg-green-500/5'
                    : 'border-white/10 bg-white/5'
                }`}
              >
                {/* Seller items list */}
                <div className="mb-3">
                  <p className="text-xs text-gray-500 uppercase tracking-widest mb-1 font-mono">
                    Seller · {link.sellerPhone}
                  </p>
                  <ul className="space-y-0.5">
                    {link.items.map((item, j) => (
                      <li key={j} className="text-sm text-gray-300 flex justify-between">
                        <span>{item.name} x{item.quantity}</span>
                        <span className="text-green-400 font-medium">
                          ₦{(item.price * item.quantity).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleWhatsApp(link)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] text-black font-bold text-sm transition-all duration-200 hover:scale-[1.02] shadow-lg shadow-[#25D366]/20"
                  >
                    <MessageCircle size={16} />
                    WhatsApp
                  </button>
                  <button
                    onClick={() => handleSMS(link)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-white/20 transition-all duration-200 hover:scale-[1.02]"
                  >
                    <Phone size={16} />
                    SMS
                  </button>
                </div>

                {done && (
                  <p className="text-green-400 text-xs mt-2 text-center font-mono">
                    ✓ Contacted
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* No seller phones found */}
        {sellerLinks.length === 0 && (
          <div className="text-center py-4 text-gray-400 text-sm">
            No seller contact info available. Check your email for order details.
          </div>
        )}

        {/* Done button */}
        <motion.button
          onClick={onDone}
          className={`mt-5 w-full py-3 rounded-xl font-extrabold text-sm transition-all duration-300 ${
            allContacted || sellerLinks.length === 0
              ? 'bg-green-500 hover:bg-green-400 text-black shadow-lg shadow-green-500/30'
              : 'bg-white/10 hover:bg-white/20 text-gray-300 border border-white/20'
          }`}
          whileTap={{ scale: 0.97 }}
        >
          {allContacted || sellerLinks.length === 0 ? '✓ All Done — Go to Orders' : 'Skip & Go to Orders'}
        </motion.button>

        <p className="text-center text-gray-600 text-[10px] font-mono mt-2">
          WhatsApp opens first · falls back to SMS if not installed
        </p>
      </motion.div>
    </motion.div>
  );
};

// ──────────────────────────────────────────────────────────────
// Main CheckoutPage
// ──────────────────────────────────────────────────────────────

const CheckoutPage = (props) => {
  const { cartItems, clearCart } = useCart();
  const [selectedOption, setSelectedOption] = useState('pickup');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState({ text: '', type: 'success' });

  // Post-payment seller contact state
  const [sellerLinks, setSellerLinks] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);

  const navigate = useNavigate();

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = 50;
  const netAmount = subtotal + tax;

  let chargedAmount = (netAmount + 100) / (1 - 0.015);
  if (chargedAmount < 2500) {
    chargedAmount = netAmount / (1 - 0.015);
  }
  const estimatedFeeUnCapped = 0.015 * chargedAmount + (chargedAmount >= 2500 ? 100 : 0);
  if (estimatedFeeUnCapped > 2000) {
    chargedAmount = netAmount + 2000;
  }
  chargedAmount = Math.ceil(chargedAmount);
  const paystackFee = chargedAmount - netAmount;

  const groupedByPickup = cartItems.reduce((groups, item) => {
    const station = item.DeliveryAddress || "University of Nigeria (call for full address)";
    if (!groups[station]) groups[station] = [];
    groups[station].push(item);
    return groups;
  }, {});

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  const handlePayment = () => {
    if (!window.PaystackPop) {
      setPopupMessage({ text: "Payment service unavailable. Try again later.", type: 'error' });
      setShowPopup(true);
      return;
    }
    if (!name || !phoneNumber || !address || !auth.currentUser?.email) {
      setPopupMessage({ text: "Please fill all fields and ensure you are logged in.", type: 'error' });
      setShowPopup(true);
      return;
    }

    const handler = window.PaystackPop.setup({
      key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
      email: auth.currentUser.email,
      amount: chargedAmount * 100,
      currency: 'NGN',
      ref: '' + Math.floor(Math.random() * 1000000000 + 1),
      metadata: {
        name,
        phone: phoneNumber,
        address,
        cartItems: cartItems.map(item => ({ id: item.id, name: item.name, qty: item.quantity }))
      },
      callback(response) {
        processPayment(response);
      },
      onClose() {
        setPopupMessage({ text: 'Transaction cancelled.', type: 'error' });
        setShowPopup(true);
      },
    });

    handler.openIframe();
  };

  const processPayment = async (response) => {
    setLoading(true);

    if (!auth.currentUser) {
      setPopupMessage({ text: "Authentication error: Please log in again.", type: 'error' });
      setShowPopup(true);
      setLoading(false);
      return;
    }

    try {
      // ========================================
      // ✅ KEY ADDITION: REFERRAL EARNING CHECK
      // ========================================

      // 1. Get buyer's current profile
      const buyerRef = doc(db, "profiles", auth.currentUser.uid);
      const buyerSnap = await getDoc(buyerRef);

      if (!buyerSnap.exists()) {
        console.error("Buyer profile not found");
        // Continue with order anyway - don't fail checkout
      } else {
        const buyerData = buyerSnap.data();

        // 2. Check conditions for earning
        const isFirstPurchase = !buyerData.hasPurchased;   // True if hasPurchased == false
        const wasReferred = !!buyerData.referredBy;        // True if referredBy is set

        // 3. Credit referrer if both conditions met
        if (isFirstPurchase && wasReferred) {
          const referrerId = buyerData.referredBy;

          try {
            const referrerRef = doc(db, "profiles", referrerId);

            // Update referrer's profile with increments
            await updateDoc(referrerRef, {
              totalEarnings: increment(300),  // +₦300 for buyer referral
              buyerReferrals: increment(1),   // +1 to count
            });

            console.log(`✅ Referrer ${referrerId} credited ₦300 for buyer action`);
            toast.success("Your referrer earned ₦300! 🎉");
          } catch (referrerError) {
            console.error("Error crediting referrer:", referrerError);
            // Don't fail the checkout if referrer credit fails
            toast.warn("Order processing but referrer credit failed. Contact support if needed.");
          }
        }

        // ========================================
        // 4. Mark buyer as "has purchased"
        // (This must happen for every order!)
        // ========================================
        try {
          await updateDoc(buyerRef, {
            hasPurchased: true,  // KEY: Set to true so this never happens again
          });
          console.log("✓ Buyer marked as hasPurchased");
        } catch (err) {
          console.error("Error marking hasPurchased:", err);
          // Don't fail checkout if this fails
        }
      }

      // ========================================
      // 5. Continue with normal order creation
      // ========================================

      const orderData = {
        name,
        buyerId: auth.currentUser.uid,
        email: auth.currentUser.email,
        phoneNumber,
        address,
        total: chargedAmount,
        paystackRef: response.reference,
        received: false,
        reminderSent: false,
        createdAt: Timestamp.now(),
        items: cartItems.map(item => ({
          id: item.id || "",
          name: item.name || "",
          sellerEmail: item.createdBy || "",
          price: item.price || 0,
          quantity: item.quantity || 1,
          sellerId: item.sellerId || "",
          BankName: item.BankName || "Opay",
          BankCode: item.BankCode || "099",
          AccountName: item.AccountName || "Ebere Alaga",
          AccountNumber: item.AccountNumber || 8142397718,
          phoneNumber: item.phoneNumber || "",
          DeliveryAddress: item.DeliveryAddress || "University of Nigeria",
          delivered: false,
          payoutDone: false,
        })),
      };

      const emailParams = {
        name,
        email: auth.currentUser.email,
        phone: phoneNumber,
        address,
      };

      const formatSellerMessage = (items, customerName, customerPhone, customerAddress) => {
        const pickupStation = items[0]?.DeliveryAddress || "University of Nigeria";
        const sellerPhone = items[0]?.phoneNumber || "N/A";
        const itemList = items
          .map(item => `  - ${item.name} x${item.quantity} (₦${item.price.toLocaleString()})`)
          .join("\n");
        return `
🛒 NEW ORDER RECEIVED — uCart

━━━━━━━━━━━━━━━━━━━━━━━━
CUSTOMER DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━
Name:    ${customerName}
Phone:   ${customerPhone}
Address: ${customerAddress}

━━━━━━━━━━━━━━━━━━━━━━━━
ITEMS ORDERED FROM YOU
━━━━━━━━━━━━━━━━━━━━━━━━
${itemList}

━━━━━━━━━━━━━━━━━━━━━━━━
PICKUP INFO
━━━━━━━━━━━━━━━━━━━━━━━━
Pickup Station:    ${pickupStation}
Your Phone Number: ${sellerPhone}

Please prepare the package and coordinate pickup with the customer.

— uCart
        `.trim();
      };

      const formatCustomerMessage = (items, customerAddress) => {
        const uniqueStations = items.reduce((map, item) => {
          const station = item.DeliveryAddress || "University of Nigeria";
          const phone = item.phoneNumber || "N/A";
          if (!map[station]) map[station] = { phones: new Set(), items: [] };
          map[station].phones.add(phone);
          map[station].items.push(item);
          return map;
        }, {});
        const itemList = items
          .map(item => `  - ${item.name} x${item.quantity} (₦${item.price.toLocaleString()})`)
          .join("\n");
        let pickupSections = "";
        Object.entries(uniqueStations).forEach(([station, data]) => {
          const phones = [...data.phones].filter(p => p !== "N/A").join(", ") || "N/A";
          const stationItems = data.items.map(i => `    • ${i.name}`).join("\n");
          pickupSections += `\n  Station:         ${station}\n  Seller Phone:    ${phones}\n  Items here:\n${stationItems}\n`;
        });
        return `
Thank you for your order on uCart! 🎉

━━━━━━━━━━━━━━━━━━━━━━━━
YOUR ORDER
━━━━━━━━━━━━━━━━━━━━━━━━
${itemList}

Your address on file: ${customerAddress}

━━━━━━━━━━━━━━━━━━━━━━━━
PICKUP DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━
${pickupSections}
Please pick up your item(s) within 48 hours.
Contact the seller using the phone number above to arrange pickup.

— uCart
        `.trim();
      };

      const sendOrderEmails = async (emailParams, cartItems) => {
        const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
        const customerTemplateId = process.env.REACT_APP_EMAILJS_CUSTOMER_TEMPLATE_ID;
        const sellerTemplateId = process.env.REACT_APP_EMAILJS_SELLER_TEMPLATE_ID;
        const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
        if (!customerTemplateId || !sellerTemplateId || !serviceId || !publicKey) return;
        try {
          await emailjs.send(serviceId, customerTemplateId, {
            ...emailParams,
            message: formatCustomerMessage(cartItems, emailParams.address),
          }, publicKey);

          const sellerMap = {};
          cartItems.forEach(item => {
            if (item.createdBy?.trim()) {
              if (!sellerMap[item.createdBy]) sellerMap[item.createdBy] = [];
              sellerMap[item.createdBy].push(item);
            }
          });
          for (const [sellerEmail, sellerItems] of Object.entries(sellerMap)) {
            await emailjs.send(serviceId, sellerTemplateId, {
              buyer_name: emailParams.name,
              buyer_phone: emailParams.phone,
              delivery_address: emailParams.address,
              seller_email_recipient: sellerEmail,
              message: formatSellerMessage(sellerItems, emailParams.name, emailParams.phone, emailParams.address),
            }, publicKey);
          }
        } catch (err) {
          console.error("Error sending emails:", err);
        }
      };

      addDoc(collection(db, 'Orders'), orderData)
        .then(async (docRef) => {
          const orderId = docRef.id;

          // Send emails (non-blocking)
          sendOrderEmails(emailParams, cartItems).catch(console.error);

          clearCart();

          // Build seller WhatsApp/SMS links
          const links = buildSellerLinks(cartItems, orderId, name, phoneNumber);
          setSellerLinks(links);
          setShowContactModal(true);
        })
        .catch((err) => {
          console.error('Order error:', err);
          setPopupMessage({ text: 'Order failed: ' + err.message, type: 'error' });
          setShowPopup(true);
        })
        .finally(() => {
          setLoading(false);
        });

    } catch (error) {
      console.error("Checkout error:", error);
      setPopupMessage({ text: "Error processing order. Please try again.", type: 'error' });
      setShowPopup(true);
      setLoading(false);
    }
  };

  const handleContactModalDone = () => {
    setShowContactModal(false);
    navigate("/thanks");
  };

  // Alert Popup
  const AlertPopup = ({ message, type, onClose }) => {
    const Icon = type === 'success' ? CheckCircle : XCircle;
    const colorClass = type === 'success' ? 'bg-green-500' : 'bg-red-500';
    useEffect(() => {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }, [onClose]);
    return (
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.8 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.8 }}
        className={`fixed bottom-6 right-6 ${colorClass} text-white px-6 py-4 rounded-xl shadow-lg flex items-center space-x-3 z-50`}
      >
        <Icon size={24} />
        <span>{message}</span>
      </motion.div>
    );
  };

  return props.isSigned ? (
    <div className="min-h-screen flex items-center justify-center bg-transparent px-4 py-10 font-['Space_Grotesk']">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-4xl bg-white/10 backdrop-blur-lg border border-white/20 shadow-xl rounded-3xl p-6 sm:p-10 text-white"
      >
        <div className="flex flex-col md:flex-row-reverse gap-8">
          {/* Right Column */}
          <div className="md:w-1/3 space-y-6">
            <div className="bg-white/5 rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-4 text-green-400">Order Summary</h3>
              <ul className="space-y-3 mb-4 text-sm text-gray-200">
                {Object.entries(groupedByPickup).map(([station, items]) => (
                  <li key={station} className="border-b border-white/10 pb-2 last:border-b-0">
                    <div className="flex items-center space-x-2 text-green-300 mb-1">
                      <Package size={16} />
                      <span className="font-semibold">Items for {station}</span>
                    </div>
                    {items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-white/80">
                        <span className="truncate">{item.name} x{item.quantity}</span>
                        <span className="text-green-300 font-medium">₦{item.price.toLocaleString()}</span>
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
              <div className="space-y-2 mt-6 pt-4 border-t border-green-500">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span className="text-green-300">₦{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tax:</span>
                  <span className="text-green-300">₦{tax.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Paystack Commission:</span>
                  <span className="text-green-300">₦{paystackFee.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/10">
                  <span>Total:</span>
                  <span className="text-green-400">₦{chargedAmount.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <motion.div
              className="bg-white/5 rounded-2xl p-6 border border-white/20 space-y-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h3 className="text-xl font-bold mb-4 text-green-400">Why Pay Here?</h3>
              <div className="flex items-center space-x-4">
                <Lock size={28} className="text-green-500 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Secure Payment</h4>
                  <p className="text-sm text-gray-200">Your information is encrypted and processed securely.</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <ShieldCheck size={28} className="text-green-500 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Trusted & Verified</h4>
                  <p className="text-sm text-gray-200">All sellers are vetted to ensure quality and reliability.</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <FastForward size={28} className="text-green-500 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Quick & Easy</h4>
                  <p className="text-sm text-gray-200">Complete your order in just a few clicks.</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Left Column */}
          <div className="md:w-2/3">
            <h2 className="text-4xl font-extrabold mb-8 text-green-400 drop-shadow-lg">Checkout</h2>
            <form onSubmit={(e) => { e.preventDefault(); handlePayment(); }} className="space-y-6">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="bg-white/5 rounded-2xl p-6 border border-white/20 space-y-4"
              >
                <h3 className="text-xl font-bold text-white">Your Information</h3>
                <div>
                  <label className="block mb-1 text-sm text-gray-300">Full Name</label>
                  <input
                    type="text"
                    className="w-full bg-white/10 border border-white/20 text-white px-4 py-2 rounded-xl placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-300">Phone Number</label>
                  <input
                    type="tel"
                    className="w-full bg-white/10 border border-white/20 text-white px-4 py-2 rounded-xl placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors"
                    placeholder="08012345678"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm text-gray-300">Delivery Address</label>
                  <textarea
                    className="w-full bg-white/10 border border-white/20 text-white px-4 py-2 rounded-xl placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors h-24"
                    placeholder="Enter your street, city, and state"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-white mb-2">Choose Order Method</h3>
                <div
                  className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${
                    selectedOption === 'pickup' ? 'border-green-500 bg-white/10' : 'border-white/20 hover:bg-white/5'
                  }`}
                  onClick={() => setSelectedOption('pickup')}
                >
                  <div>
                    <h4 className="font-semibold text-white">Pickup</h4>
                    <p className="text-sm text-gray-200">Come pick up your item(s) from the designated station.</p>
                  </div>
                  <CheckCircle size={24} className={selectedOption === 'pickup' ? "text-green-400" : "text-gray-400"} />
                </div>
                <div className="flex items-center justify-between p-4 border rounded-xl bg-gray-600/20 border-gray-500/50 opacity-50 cursor-not-allowed">
                  <div>
                    <h4 className="font-medium text-gray-400">Delivery</h4>
                    <p className="text-sm text-gray-300">Not available at the moment</p>
                  </div>
                  <XCircle size={24} className="text-red-400" />
                </div>
              </motion.div>

              <motion.button
                type="submit"
                disabled={loading || cartItems.length === 0}
                className="w-full bg-green-600 hover:bg-green-700 transition-all duration-300 text-white py-3 px-4 rounded-xl font-bold text-lg shadow-lg disabled:bg-gray-500 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Lock size={20} />
                    <span>Pay ₦{chargedAmount.toLocaleString()} with Paystack</span>
                  </>
                )}
              </motion.button>
            </form>
          </div>
        </div>
      </motion.div>

      {/* Contact Sellers Modal */}
      <AnimatePresence>
        {showContactModal && (
          <ContactSellersModal
            sellerLinks={sellerLinks}
            onDone={handleContactModalDone}
          />
        )}
      </AnimatePresence>

      {/* Alert Popup */}
      <AnimatePresence>
        {showPopup && (
          <AlertPopup
            message={popupMessage.text}
            type={popupMessage.type}
            onClose={() => setShowPopup(false)}
          />
        )}
      </AnimatePresence>
    </div>
  ) : (
    <div className="min-h-screen flex items-center justify-center bg-transparent px-4 py-10">
      <div className="bg-white/10 backdrop-blur-md text-white p-6 rounded-2xl shadow-lg w-full max-w-sm border border-white/20 text-center">
        <h2 className="text-2xl font-bold mb-2">Hold on!</h2>
        <p className="text-base text-gray-200 mb-4">You are not signed in.</p>
        <Link to="/Login" className="inline-block w-full py-2 px-4 bg-green-600 hover:bg-green-700 transition rounded-lg text-white font-medium">
          Sign In
        </Link>
      </div>
    </div>
  );
};

export default CheckoutPage;